import type { User } from 'oidc-client-ts';

/**
 * The single path for validating and refreshing the OIDC access token.
 *
 * Every consumer funnels through `getFreshAccessToken()`:
 *  - the api client before each request (proactive, threshold-based)
 *  - the api client's 401 handler (`forceRenew` — the server rejected the token)
 *  - `userManager`'s accessTokenExpiring/Expired subscriptions (proactive timer)
 *  - `OidcSessionProvider`'s expired-on-load recovery
 *
 * Renewals are single-flight: concurrent callers share ONE `signinSilent()`,
 * so the rotating refresh token (Duende `RefreshTokenUsage.OneTimeOnly`) is
 * never consumed twice in parallel — consuming it twice yields an
 * unrecoverable invalid_grant. A fatal grant error drops the stale user, so a
 * dead refresh token is retried at most once — never a renewal loop.
 *
 * Mirrors `Web/src/auth/tokenRefresh.ts`, minus its legacy cookie mirroring.
 * Kept free of runtime imports (only `import type`) so it stays loadable in
 * the node-env unit suite — the wired instance lives in `userManager.ts`.
 */

const RENEW_THRESHOLD_SECONDS = 30;

// A dead/rotated/revoked refresh token is unrecoverable — retrying it loops forever.
export const isFatalRenewalError = (err: unknown): boolean => {
  const code = (err as { error?: string })?.error ?? '';
  const message = err instanceof Error ? err.message : String(err);
  return /invalid_grant|invalid_token|unauthorized_client/i.test(`${code} ${message}`);
};

export type TokenRefreshDeps = {
  getUser: () => Promise<User | null>;
  signinSilent: () => Promise<User | null>;
  removeUser: () => Promise<void>;
};

export type TokenRefresh = {
  getFreshAccessToken: (opts?: { forceRenew?: boolean }) => Promise<string | undefined>;
};

export const createTokenRefresh = (deps: TokenRefreshDeps): TokenRefresh => {
  let inFlightRenewal: Promise<User | null> | null = null;

  const renewOnce = async (): Promise<User | null> => {
    try {
      return await deps.signinSilent();
    } catch (err) {
      if (isFatalRenewalError(err)) {
        // The refresh token is definitively dead. Drop the stale user so (a)
        // nothing re-attempts the same grant — the next call sees "no user"
        // and bails, and (b) `isAuthenticated` flips false so ProtectedRoute
        // shows a clean sign-in gate.
        await deps.removeUser().catch(() => {});
      }
      // Transient failure (network/timeout): keep the user so a later caller
      // can retry. Either way this attempt yields no token.
      return null;
    }
  };

  const renewSingleFlight = (): Promise<User | null> => {
    if (!inFlightRenewal) {
      inFlightRenewal = renewOnce().finally(() => {
        inFlightRenewal = null;
      });
    }
    return inFlightRenewal;
  };

  /**
   * Returns a usable access token, renewing first when the stored one is
   * missing or within the renew threshold of expiry. Pass `forceRenew` when
   * the server has rejected the current token (401) — local expiry belief is
   * irrelevant then. Resolves `undefined` (never rejects) when there is no
   * session yet or renewal fails, so an auth failure surfaces downstream as a
   * plain 401 rather than an opaque rejection React Query would retry.
   */
  const getFreshAccessToken = async ({
    forceRenew = false,
  }: { forceRenew?: boolean } = {}): Promise<string | undefined> => {
    // A storage failure reading the user is treated as "no session".
    const user = await deps.getUser().catch(() => null);
    // No stored session — do NOT attempt a renewal. Preserves the clean
    // bail-out during the initial login redirect, and (after a fatal
    // removeUser) stops repeat attempts against a dead grant.
    if (!user) return undefined;

    if (!forceRenew && user.access_token && (user.expires_in ?? -1) > RENEW_THRESHOLD_SECONDS) {
      return user.access_token;
    }

    const renewed = await renewSingleFlight();
    return renewed?.access_token ?? undefined;
  };

  return { getFreshAccessToken };
};
